import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from '../../api/axios';
import LoadingScreen from '../../layout/LoadingScreen';
import Toast from '../../ui/Toast';
import EmailComposerModal from '../outreach/EmailComposerModal';
import ProspectDetailModal from './ProspectDetailModal';
import Pagination from '../../ui/Pagination';
import Banner from '../../ui/Banner';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import './ProspectListView.css';

// ─── Palette for deterministic avatar colors (no external API calls) ──────────
const AV_FILLS = ['#E6F1FB', '#EAF3DE', '#FAEEDA', '#FBEAF0', '#E1F5EE', '#EEEDFE', '#FAECE7'];
const AV_TEXT = ['#0C447C', '#27500A', '#633806', '#72243E', '#085041', '#3C3489', '#712B13'];

function hashColor(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) % AV_FILLS.length;
  }
  return [AV_FILLS[h], AV_TEXT[h]];
}

function initials(name = '') {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

// ─── Data helpers ──────────────────────────────────────────────────────────────
function isMasked(value) {
  const t = String(value ?? '').trim();
  return !t || /(\.\.\.|…|\*{2,}|x{2,}|X{2,})/.test(t);
}

function normalizeValues(value) {
  return (Array.isArray(value) ? value : [value])
    .map((item) =>
      typeof item === 'object' && item !== null
        ? String(item.email || item.number || item.value || '').trim()
        : String(item ?? '').trim()
    )
    .filter((v) => v && !isMasked(v));
}

function getCompany(prospect) {
  let details = prospect.company_details ?? null;
  if (typeof details === 'string') {
    try {
      details = JSON.parse(details);
    } catch {
      details = null;
    }
  }
  return {
    name:
      prospect.company_name ??
      details?.companyName ??
      details?.name ??
      details?.company?.name ??
      'Unknown',
    logo:
      details?.logoUrl ??
      details?.logo_url ??
      details?.logo ??
      details?.companyLogo ??
      details?.company?.logoUrl ??
      null,
  };
}

// ─── Presentational sub-components ────────────────────────────────────────────
function Avatar({ name, size = 32 }) {
  const [bg, fg] = hashColor(name);
  return (
    <div
      className="plv-av"
      style={{
        width: size,
        height: size,
        background: bg,
        color: fg,
        fontSize: Math.floor(size * 0.36),
      }}
      aria-hidden
    >
      {initials(name)}
    </div>
  );
}

function CompanyMark({ name, logoUrl }) {
  const [bg, fg] = hashColor(name);
  const [imgErr, setImgErr] = useState(false);
  if (logoUrl && !imgErr) {
    return (
      <div className="plv-co-mark">
        <img src={logoUrl} alt="" onError={() => setImgErr(true)} />
      </div>
    );
  }
  return (
    <div className="plv-co-mark" style={{ background: bg, color: fg }}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

function ContactCell({ prospect }) {
  const emails = normalizeValues(prospect.email);
  const phones = normalizeValues(prospect.phone_number);

  if (!emails.length && !phones.length) {
    return <span className="plv-na">No contact data</span>;
  }

  return (
    <div className="plv-contact">
      {emails[0] && (
        <div className="plv-contact-row">
          <i className="fas fa-envelope" />
          <span>{emails[0]}</span>
          {emails.length > 1 && <span className="plv-plus">+{emails.length - 1}</span>}
          {/* Note: Provider badges removed as requested */}
        </div>
      )}
      {phones[0] && (
        <div className="plv-contact-row">
          <i className="fas fa-phone-alt" />
          <span>{phones[0]}</span>
          {phones.length > 1 && <span className="plv-plus">+{phones.length - 1}</span>}
        </div>
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export default function ProspectListView({ userId }) {
  const { isOrganizationWorkspace, isOrganizationAdmin, workspaceRole } = useWorkspace();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
  const [composerOpen, setComposerOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const abortRef = useRef(null);

  const showToast = (message, type = 'error') => setToast({ show: true, message, type });

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchProspects = useCallback(
    async (isRefresh = false) => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      isRefresh ? setRefreshing(true) : setLoading(true);
      try {
        const res = await axios.get(`/prospects?user_id=${userId}`, {
          signal: abortRef.current.signal,
        });
        setProspects(res.data.success ? res.data.data : []);
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('Error fetching prospects:', e);
          setProspects([]);
        }
      } finally {
        isRefresh ? setRefreshing(false) : setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    fetchProspects();
    return () => abortRef.current?.abort();
  }, [fetchProspects]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [query, perPage]);

  // ── Derived ──────────────────────────────────────────────────────────────
  const filtered = useMemo(
    () =>
      prospects.filter(
        (p) =>
          p.name?.toLowerCase().includes(query.toLowerCase()) ||
          p.company_name?.toLowerCase().includes(query.toLowerCase())
      ),
    [prospects, query]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const start = (page - 1) * perPage;
  const pageItems = filtered.slice(start, start + perPage);
  const pageIds = pageItems.map((p) => p.id);

  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));

  // ── Selection ────────────────────────────────────────────────────────────
  const toggleAllOnPage = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleEmail = () => {
    if (selectedIds.size === 0) {
      showToast('Select at least one prospect first');
      return;
    }
    setComposerOpen(true);
  };

  const handleVisibilityChange = async (prospectId, newVisibility) => {
    try {
      const response = await axios.patch(`/prospects/${prospectId}/visibility`, {
        visibility: newVisibility,
      });
      if (response.data.success) {
        setProspects((prev) =>
          prev.map((p) => (p.id === prospectId ? { ...p, visibility: newVisibility } : p))
        );
        showToast(
          `Prospect visible to ${newVisibility === 'everyone' ? 'Everyone' : 'Admins Only'}`,
          'success'
        );
      }
    } catch (err) {
      console.error('Error updating prospect visibility:', err);
      showToast('Failed to update visibility.');
    }
  };

  const handleExport = () => {
    if (selectedIds.size === 0) {
      showToast('Select prospects to export');
      return;
    }

    const rows = prospects.filter((p) => selectedIds.has(p.id));
    const headers = ['Name', 'Job Title', 'Company', 'Email', 'Phone', 'Location', 'LinkedIn'];

    // Escape inner content first, then wrap in quotes
    const escapeCSV = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;

    // Safely extract and join job titles (handles string, array of strings, array of objects, null/undefined)
    const getJobTitles = (p) => {
      const val = p.job_title;
      if (!val) return '';
      if (Array.isArray(val)) {
        return val
          .map((item) =>
            typeof item === 'string' ? item : item?.title || item?.name || item?.job_title || ''
          )
          .filter(Boolean)
          .join('; ');
      }
      return String(val);
    };

    // Safely extract location string
    const getLocation = (p) => {
      if (!p.location) return '';
      const loc = p.location;
      if (typeof loc === 'string') return loc;
      return loc.country || loc.state || loc.city || loc.raw_location || '';
    };

    const csvContent = [
      headers.join(','),
      ...rows.map((p) =>
        [
          escapeCSV(p.name),
          escapeCSV(getJobTitles(p)),
          escapeCSV(getCompany(p).name),
          escapeCSV(normalizeValues(p.email).join('; ')),
          escapeCSV(normalizeValues(p.phone_number).join('; ')),
          escapeCSV(getLocation(p)),
          escapeCSV(p.linkedin_url),
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'prospects_export.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRowClick = (e, prospect) => {
    if (e.target.type === 'checkbox' || e.target.closest('a') || e.target.tagName === 'BUTTON')
      return;
    setViewing(prospect);
  };

  // ── Render ──
  return (
    <div className="plv-page">
      <Banner
        title="Prospect List"
        subtitle="View, manage, and export your saved leads for outreach."
        type="page-header"
      />

      {/* Toolbar */}
      <div className="plv-toolbar">
        <div className="plv-search">
          <i className="fas fa-search plv-search-icon" aria-hidden />
          <input
            type="text"
            placeholder="Search by name or company…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search prospects"
          />
          {query && (
            <button
              className="plv-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              <i className="fas fa-times" />
            </button>
          )}
        </div>

        {selectedIds.size > 0 && (
          <div className="plv-sel-pill" role="status" aria-live="polite">
            <i className="fas fa-check-circle" aria-hidden />
            <span>{selectedIds.size} selected</span>
            <button onClick={clearSelection}>Clear</button>
          </div>
        )}

        <div className="plv-actions">
          <button
            className="plv-btn plv-btn-outline plv-btn-icon"
            onClick={() => fetchProspects(true)}
            disabled={refreshing}
            title="Refresh"
            aria-label="Refresh list"
          >
            <i className={`fas fa-sync-alt ${refreshing ? 'fa-spin' : ''}`} />
          </button>
          <button className="plv-btn plv-btn-outline" onClick={handleEmail}>
            <i className="fas fa-envelope" /> Send Email
          </button>
          <button className="plv-btn plv-btn-primary" onClick={handleExport}>
            <i className="fas fa-download" /> Export CSV
          </button>
        </div>
      </div>

      {/* Unified Data Card with Pagination */}
      <Pagination
        columns={[
          {
            key: 'checkbox',
            label: (
              <input
                type="checkbox"
                checked={allPageSelected}
                ref={(el) => {
                  if (el) el.indeterminate = somePageSelected && !allPageSelected;
                }}
                onChange={(e) => toggleAllOnPage(e.target.checked)}
                aria-label="Select all on this page"
              />
            ),
            width: '50px',
            className: 'plv-col-chk',
          },
          { key: 'prospect', label: 'Prospect', sortable: true },
          { key: 'company', label: 'Company', sortable: true },
          { key: 'contact', label: 'Contact Data' },
          ...(isOrganizationWorkspace
            ? [{ key: 'visibility', label: 'Visibility', width: '140px' }]
            : []),
          { key: 'outreach', label: 'Outreach', width: '120px' },
          { key: 'arrow', label: '', width: '40px', className: 'plv-col-arrow' },
        ]}
        data={pageItems}
        renderRow={(prospect) => {
          const { name: coName, logo: coLogo } = getCompany(prospect);
          const isSel = selectedIds.has(prospect.id);
          return (
            <tr
              key={prospect.id}
              className={isSel ? 'plv-selected' : ''}
              onClick={(e) => handleRowClick(e, prospect)}
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleRowClick(e, prospect)}
              aria-selected={isSel}
            >
              <td className="plv-col-chk" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => toggleRow(prospect.id)}
                  aria-label={`Select ${prospect.name}`}
                />
              </td>
              <td>
                <div className="plv-prospect-cell">
                  <Avatar name={prospect.name} />
                  <div>
                    <div className="plv-name">{prospect.name}</div>
                    {prospect.job_title && (
                      <div className="plv-job-title">{prospect.job_title}</div>
                    )}
                  </div>
                </div>
              </td>
              <td>
                <div className="plv-co-cell">
                  <CompanyMark name={coName} logoUrl={coLogo} />
                  <span>{coName}</span>
                </div>
              </td>
              <td>
                <ContactCell prospect={prospect} />
              </td>
              {isOrganizationWorkspace && (
                <td>
                  {isOrganizationAdmin ? (
                    <select
                      className="plv-visibility-select"
                      value={prospect.visibility === 'everyone' ? 'everyone' : 'admins'}
                      onChange={(e) => handleVisibilityChange(prospect.id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {(workspaceRole === 'org_owner' || workspaceRole === 'org_admin') && (
                        <>
                          <option value="admins">Admins Only</option>
                          <option value="everyone">Everyone</option>
                        </>
                      )}
                      {!workspaceRole && <option value="admins">Admins Only</option>}
                    </select>
                  ) : (
                    <span className="plv-visibility-text">
                      {prospect.visibility === 'everyone' ? 'Everyone' : 'Admins Only'}
                    </span>
                  )}
                </td>
              )}
              <td>
                <div className="plv-outreach">
                  {prospect.emails_sent_count ? (
                    <div className="plv-outreach-badge">
                      <i className="fas fa-envelope-open" title="Emails sent"></i>
                      <span>{prospect.emails_sent_count}</span>
                    </div>
                  ) : prospect.last_email_sent_at ? (
                    <div className="plv-outreach-status">
                      <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {new Date(prospect.last_email_sent_at).toLocaleDateString()}
                      </span>
                    </div>
                  ) : (
                    <span className="plv-no-outreach">No outreach</span>
                  )}
                </div>
              </td>
              <td className="plv-col-arrow" aria-hidden>
                <i className="fas fa-chevron-right plv-arrow" />
              </td>
            </tr>
          );
        }}
        emptyState={
          <>
            <i className="fas fa-user-slash" aria-hidden />
            <p>
              {query ? (
                <>
                  No prospects match <strong>"{query}"</strong>
                </>
              ) : (
                'Your list is empty. Add prospects from Prospect Finder.'
              )}
            </p>
          </>
        }
        loading={loading}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        itemsPerPage={perPage}
        onItemsPerPageChange={setPerPage}
        totalItems={filtered.length}
        itemsName="prospects"
        showJumpTo={filtered.length > 50}
        className="prospect-data-box"
      />

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast((t) => ({ ...t, show: false }))}
        />
      )}

      {/* Modals */}
      <EmailComposerModal
        isOpen={composerOpen}
        onClose={() => setComposerOpen(false)}
        selectedProspects={filtered.filter((p) => selectedIds.has(p.id))}
        onSent={(result) => {
          showToast(
            `Sent ${result.sent} email${result.sent !== 1 ? 's' : ''} successfully`,
            'success'
          );
          clearSelection();
        }}
      />

      <ProspectDetailModal isOpen={!!viewing} onClose={() => setViewing(null)} prospect={viewing} />
    </div>
  );
}
